import { FastAverageColor } from 'fast-average-color';
import { ForwardedRef, useCallback, useEffect, useRef, useState } from 'react';

const fastAverageColor = new FastAverageColor();

interface IUseHeroImage {
  // Merge-ref for the hero <Box>. Wires the forwarded ref (NavBar/FAB scroll positioning)
  // together with the internal node we observe, so we can measure width without stealing it.
  setHeroRef: (node: HTMLDivElement | null) => void;
  // Rendered width of the hero box (not window.innerWidth — the builder preview scales the
  // iframe and constrains mobile width via the frame, so the box width is the correct basis).
  heroWidth: number;
  // Image aspect ratio (naturalWidth / naturalHeight); 0 until measured or for SVGs with no
  // intrinsic size, in which case the renderer falls back to HERO_DEFAULT_IMAGE_ASPECT.
  imageAspect: number;
  // Average color of the loaded image, used to pick a legible text color; null on failure.
  imageAverageColor: string | null;
}

// Tracks the hero background image's measurements: the box's rendered width (via ResizeObserver)
// and, when a background image is present, its aspect ratio and average color (via an off-DOM
// Image load). Pure DOM/image side effects extracted from Hero so the component stays declarative.
const useHeroImage = (imageUrl: string | null, ref: ForwardedRef<HTMLDivElement>): IUseHeroImage => {
  const [heroWidth, setHeroWidth] = useState<number>(0);
  const [imageAspect, setImageAspect] = useState<number>(0);
  const [imageAverageColor, setImageAverageColor] = useState<string | null>(null);

  const heroNodeRef = useRef<HTMLDivElement | null>(null);

  const setHeroRef = useCallback(
    (node: HTMLDivElement | null) => {
      heroNodeRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
      if (node) {
        setHeroWidth(node.clientWidth);
      }
    },
    [ref],
  );

  useEffect(() => {
    const node = heroNodeRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) {
        setHeroWidth(width);
      }
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Reset immediately so a stale aspect/color from the previous image can't drive the
    // Adapt height or text contrast while the new image is still loading.
    setImageAspect(0);
    setImageAverageColor(null);

    if (!imageUrl) {
      return undefined;
    }

    let cancelled = false;

    // Aspect ratio only needs the intrinsic dimensions, which are readable without CORS.
    // Measure it from a plain (non-crossOrigin) load so a missing Access-Control-Allow-Origin
    // header can't zero out the aspect for an image the browser otherwise renders fine.
    const aspectImg = new Image();
    aspectImg.onload = () => {
      if (!cancelled) {
        setImageAspect(aspectImg.naturalHeight ? aspectImg.naturalWidth / aspectImg.naturalHeight : 0);
      }
    };
    aspectImg.src = imageUrl;

    // Average color needs pixel access, which requires a CORS-enabled (crossOrigin) load;
    // a CORS failure degrades color only (falls back to the default text color), not aspect.
    const colorImg = new Image();
    colorImg.crossOrigin = 'anonymous';
    colorImg.onload = () => {
      if (cancelled) {
        return;
      }
      // getColor is synchronous on an already-loaded element and reports failure via
      // result.error (falling back to its default) rather than throwing.
      const color = fastAverageColor.getColor(colorImg);
      setImageAverageColor(color.error ? null : color.hex);
    };
    colorImg.onerror = () => {
      if (!cancelled) {
        setImageAverageColor(null);
      }
    };
    colorImg.src = imageUrl;

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  return { setHeroRef, heroWidth, imageAspect, imageAverageColor };
};

export default useHeroImage;
